<template>
  <div
    class="welcome-view-v2"
    :class="{ 'welcome-view-v2--compact': !isGridLayout }"
    style="background: var(--oliver-gradient)"
  >
    <div class="ambient-orb" aria-hidden="true"></div>
    <welcome-top-bar
      :is-fullscreen="isFullscreen"
      :is-customized="isCustomized"
      :is-wide="isGridLayout"
      :is-docked="isDocked"
      :device-type="deviceType"
      @home="handleHome"
      @apps="handleApps"
      @toggle-nav-sidebar="$emit('toggle-nav-sidebar')"
      @new-chat="$emit('create-chat')"
      @show-history="$emit('show-history')"
      @toggle-history="$emit('toggle-history')"
      @toggle-dark-mode="handleToggleDarkMode"
      @toggle-dock="$emit('toggle-dock')"
      @reset-size="$emit('reset-size')"
      @toggle-fullscreen="$emit('toggle-fullscreen')"
      @close="$emit('close')"
      @start-popover-drag="handleHeaderDrag"
    />

    <div class="welcome-body">
      <history-sidebar
        :is-open="isHistoryOpen"
        :overlay="isOverlay"
        @close="$emit('close-history')"
        @select-chat="$emit('select-chat-from-history', $event)"
        @new-chat="$emit('new-chat-from-history')"
      />

      <div class="welcome-main">
        <div class="welcome-body-inner">
          <div class="welcome-content">
            <div class="greeting-row">
              <div class="greeting-avatar-wrap">
                <img
                  v-if="!avatarFallback"
                  :src="avatarSrc"
                  class="greeting-avatar-img"
                  alt="Profile"
                  @error="avatarFallback = true"
                />
                <div v-else class="greeting-avatar-fallback">
                  {{ initials }}
                </div>
              </div>
              <div class="greeting-text">
                <h1 class="greeting-title">{{ greeting }}, {{ firstName }}!</h1>
                <p class="greeting-subtitle">How can I assist you today?</p>
              </div>
              <div class="greeting-logo-wrap">
                <img
                  :src="companyLogo || companyLogoPlaceholder"
                  class="greeting-logo-img"
                  alt="Company logo"
                />
              </div>
            </div>

            <div class="cards-grid">
              <upcoming-appointments-card
                class="cards-grid-item"
                @view-all="handleViewAllAppointments"
              />
              <timeline-messages-card
                class="cards-grid-item"
                @view-all="handleViewAllMessages"
              />
              <a-i-organizer-card class="cards-grid-item" />
              <clients-card
                class="cards-grid-item"
                @select-patient="handleSelectPatient"
              />
            </div>
          </div>
        </div>

        <div class="chat-input-wrap">
          <div class="chat-input-inner">
            <chat-input
              ref="chatInput"
              inline
              :placeholder="chatInputPlaceholder"
              :disabled="isCreatingChat || isTranscribing"
              :is-transcribing="isTranscribing"
              :show-patient-mode="showPatientMode"
              :patient-mode-active="patientModeEnabled"
              :patient-mode-btn-label="patientModeBtnLabel"
              :can-switch-patient="canSwitchPatient"
              :patient-capsule-label="patientCapsuleLabel"
              :show-web-search="true"
              :web-search-active="webSearchEnabled"
              :suggestions-open="showSuggestionsPanel"
              :attachment-drafts="attachmentDrafts"
              :attachment-notices="attachmentNotices"
              :attachments-creating="attachmentsCreating"
              @send="handleSend"
              @files-selected="handleFilesSelected"
              @remove-attachment="handleRemoveAttachment"
              @retry-attachment="handleRetryAttachment"
              @dismiss-notices="handleDismissNotices"
              @audio-recorded="handleAudioRecorded"
              @toggle-suggestions="showSuggestionsPanel = !showSuggestionsPanel"
              @toggle-patient-mode="handleTogglePatientMode"
              @switch-patient-mode="$emit('switch-patient-mode')"
              @toggle-web-search="handleToggleWebSearch"
            />
            <p class="disclaimer-note">
              Oliver can make mistakes. Always verify with clinical judgement.
            </p>
          </div>
        </div>

        <suggestions-panel
          :is-open="showSuggestionsPanel"
          :is-fullscreen="isFullscreen"
          :is-wide="isWideLayout"
          :patient-mode-enabled="patientModeEnabled"
          :web-search-enabled="webSearchEnabled"
          :show-patient-mode="showPatientMode"
          mode="welcome"
          @select-command="handleCommandSelect"
          @toggle-patient-mode="handleTogglePatientMode"
          @toggle-web-search="handleToggleWebSearch"
          @close="showSuggestionsPanel = false"
          @close-oliver="$emit('close')"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ChatInput from "@/components/OliverBotV2/chat/ChatInput.vue";
import attachmentHandling from "@/components/OliverBotV2/mixins/attachmentHandling.js";
import headerDraggable from "@/components/OliverBotV2/mixins/headerDraggable.js";
import { SuggestionsPanel } from "@/components/OliverBotV2/suggestions";
import { USER_ROLES } from "@/components/OliverBotV2/suggestions/constants";
import { getPatientPlaceholder } from "@/components/OliverBotV2/utils/patientModeManager.js";
import { mapActions, mapState } from "vuex";
import AIOrganizerCard from "./cards/AIOrganizerCard.vue";
import ClientsCard from "./cards/ClientsCard.vue";
import TimelineMessagesCard from "./cards/TimelineMessagesCard.vue";
import UpcomingAppointmentsCard from "./cards/UpcomingAppointmentsCard.vue";
// import OliverAvatar from "./OliverAvatar.vue";
import WelcomeTopBar from "./WelcomeTopBar.vue";
import HistorySidebar from "@/components/OliverBotV2/shared/HistorySidebar.vue";
import { NARROW_PANEL_WIDTH } from "@/components/OliverBotV2/utils/panelLayout.js";

const AVATAR_PLACEHOLDER = require("@/assets/img/avatars/profiles/avatar.jpg");

export default {
  name: "WelcomeView",
  components: {
    ChatInput,
    SuggestionsPanel,
    WelcomeTopBar,
    HistorySidebar,
    UpcomingAppointmentsCard,
    TimelineMessagesCard,
    AIOrganizerCard,
    ClientsCard,
    // OliverAvatar,
  },
  mixins: [headerDraggable, attachmentHandling],
  props: {
    isFullscreen: {
      type: Boolean,
      default: false,
    },
    isCustomized: {
      type: Boolean,
      default: false,
    },
    isWideLayout: {
      type: Boolean,
      default: false,
    },
    isGridLayout: {
      type: Boolean,
      default: false,
    },
    isDocked: {
      type: Boolean,
      default: false,
    },
    patientModeEnabled: {
      type: Boolean,
      default: false,
    },
    showPatientMode: {
      type: Boolean,
      default: false,
    },
    patientModeBtnLabel: {
      type: String,
      default: "Client Mode",
    },
    canSwitchPatient: {
      type: Boolean,
      default: false,
    },
    patientCapsuleLabel: {
      type: String,
      default: null,
    },
    webSearchEnabled: {
      type: Boolean,
      default: false,
    },
    isHistoryOpen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showSuggestionsPanel: false,
      avatarFallback: false,
      panelWidth: Infinity,
      companyLogoPlaceholder: require("@/assets/img/c3-logo.png"),
    };
  },
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      patient: (state) => state.patients.patient,
      isCreatingChat: (state) => state.oliverChatBotV2.isCreatingChat,
      isTranscribing: (state) => state.oliverChatBotV2.isTranscribing,
      deviceType: (state) => state.settings.deviceType,
      selectedChat: (state) => state.oliverChatBotV2.selectedChat,
      companyLogo: (state) => state.settings.companyLogo,
    }),
    firstName() {
      if (this.user) {
        return this.user.first || this.user.email || "there";
      }
      return "there";
    },
    initials() {
      if (!this.user) return "U";
      const first = (this.user.first || "").trim();
      const last = (this.user.last || "").trim();
      if (first && last) return (first[0] + last[0]).toUpperCase();
      if (first) return first.slice(0, 2).toUpperCase();
      const email = (this.user.email || "U").trim();
      return email.slice(0, 2).toUpperCase();
    },
    avatarSrc() {
      return (this.user && this.user.profile_picture) || AVATAR_PLACEHOLDER;
    },
    greeting() {
      const h = new Date().getHours();
      if (h < 12) return "Good Morning";
      if (h < 17) return "Good Afternoon";
      return "Good Evening";
    },
    userRole() {
      if (!this.user) return USER_ROLES.PROVIDER;
      if (this.user.isAdmin) return USER_ROLES.ADMIN;
      if (this.user.isManagingAdmin) return USER_ROLES.MANAGER;
      return USER_ROLES.PROVIDER;
    },
    isOverlay() {
      return this.panelWidth < NARROW_PANEL_WIDTH;
    },
    chatInputPlaceholder() {
      if (this.patientModeEnabled && this.patient) {
        return getPatientPlaceholder(this.patient);
      }
      return `Ask me anything about your ${this.$filters.customerLabel(
        "plural",
        false
      )}...`;
    },
  },
  mounted() {
    this._ro = new ResizeObserver(([entry]) => {
      this.panelWidth = entry.contentRect.width;
    });
    this._ro.observe(this.$el);
  },
  beforeDestroy() {
    if (this._ro) this._ro.disconnect();
  },
  methods: {
    ...mapActions("oliverChatBotV2", [
      "transcribeAudio",
      "setDarkMode",
      "createChat",
      "setSelectedChat",
    ]),
    handleHome() {
      // TODO: scroll to top / refresh dashboard
      this.$emit("go-home");
    },
    handleApps() {
      // TODO: app launcher menu
    },
    handleToggleDarkMode(isDark) {
      this.setDarkMode(isDark);
    },
    handleViewAllAppointments() {
      this.$router.push("/calendar");
      this.$emit("close");
    },
    handleViewAllMessages() {
      if (this.user.isAdmin || this.user.isManagingAdmin) {
        this.$router.push("/timeline");
      } else if (this.user.isProvider) {
        this.$router.push(`/providers/${this.user.provider_id}/timeline`);
      } else {
        this.$router.push(`/clients/${this.user.patient_id}/timeline`);
      }
      this.$emit("close");
    },
    handleSend(payload) {
      this.$emit("create-chat-with-message", payload);
    },
    // Files dropped here need a conversation before they upload: one is created (or the
    // empty one reused, as "+ New Chat" does) once, however many files arrive at once.
    // The view stays on welcome until Send.
    chatForAttachments() {
      if (this.selectedChat) return Promise.resolve(this.selectedChat.id);
      if (!this._creatingChat) {
        this._creatingChat = (async () => {
          const response = await this.createChat();
          if (!response?.data) return null;
          await this.setSelectedChat(response.data.data);
          return this.selectedChat?.id || null;
        })().finally(() => {
          this._creatingChat = null;
        });
      }
      return this._creatingChat;
    },
    async handleAudioRecorded(audioFile) {
      try {
        const response = await this.transcribeAudio({ file: audioFile });
        if (response && response.transcription && this.$refs.chatInput) {
          this.$refs.chatInput.setInputValue(response.transcription);
        }
      } catch (error) {
        console.error("Audio transcription failed:", error);
      }
    },
    handleCommandSelect(command) {
      if (this.$refs.chatInput) {
        this.$refs.chatInput.setInputValue(command.prompt, {
          cursorAtStart: true,
        });
      }
    },
    handleTogglePatientMode(isActive) {
      this.$emit("toggle-patient-mode", isActive);
    },
    handleToggleWebSearch(isActive) {
      this.$emit("toggle-web-search", isActive);
    },
    handleSelectPatient(patient) {
      const name = `${patient.first || ""} ${patient.last || ""}`.trim();
      const prompt = `Generating ${this.$filters.customerLabel(
        "singular",
        false
      )} summary for ${name}. `;
      this.$emit("create-chat-with-message", {
        text: prompt,
        role: "assistant",
        files: [],
        patient: {
          ...patient,
          quick_summary: true,
        },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.welcome-view-v2 {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  color: var(--oliver-text);

  .orb-container {
    width: auto;
  }
}

.ambient-orb {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 35%;
  aspect-ratio: 1;
  background: radial-gradient(
    circle,
    rgba(210, 160, 120, 0.2) 0%,
    rgba(240, 200, 160, 0.1) 55%,
    transparent 75%
  );
  border-radius: 50%;
  filter: blur(24px);
  pointer-events: none;
  z-index: 0;
}

.welcome-body {
  flex: 1;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  min-height: 0;
  z-index: 1;
  position: relative;
}

.welcome-main {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

.welcome-body-inner {
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  scrollbar-width: thin;
  scrollbar-color: var(--oliver-scrollbar, rgba(0, 0, 0, 0.2)) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--oliver-scrollbar, rgba(0, 0, 0, 0.2));
    border-radius: 3px;
  }
}

.welcome-content {
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.greeting-row {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 2px;
}

.greeting-avatar-wrap {
  flex-shrink: 0;
  width: 5rem;
  height: 5rem;
  border-radius: 12px;
  overflow: hidden;
  background: var(--oliver-accent);
  border: 2px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -2px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
}

.greeting-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.greeting-avatar-fallback {
  font-size: 24px;
  font-weight: 700;
  color: #fff;
  letter-spacing: 0.5px;
}

.greeting-text {
  flex: 1;
  min-width: 0;
}

.greeting-logo-wrap {
  flex-shrink: 0;
  max-width: 90px;
  max-height: 75px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.greeting-logo-img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
}

.greeting-title {
  font-size: 20px;
  font-weight: 700;
  color: var(--oliver-text);
  margin: 0 0 3px;
  line-height: 1.25;
  letter-spacing: -0.025em;

  @media (min-width: 640px) {
    font-size: 24px;
  }

  @media (min-width: 768px) {
    font-size: 30px;
  }
}

.greeting-subtitle {
  font-size: 14px;
  font-weight: 500;
  color: var(--oliver-greeting-subtitle);
  margin: 0;

  @media (min-width: 640px) {
    font-size: 16px;
  }

  @media (min-width: 768px) {
    font-size: 18px;
  }
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-rows: 180px;
  gap: 12px;
}

.cards-grid-item {
  min-width: 0;
  height: 180px;
}

.chat-input-wrap {
  flex-shrink: 0;
  padding: 8px 24px 14px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.chat-input-inner {
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
}

/* ---------- Compact mode (popover < 700×800) ---------- */
.welcome-view-v2--compact {
  .welcome-body-inner {
    padding: 12px 14px 10px;
  }

  .welcome-content {
    gap: 12px;
  }

  .greeting-avatar-wrap {
    width: 60px;
    height: 60px;
    border-radius: 10px;
  }

  .greeting-avatar-fallback {
    font-size: 18px;
  }

  .greeting-logo-wrap {
    max-width: 60px;
    max-height: 60px;
  }

  .greeting-title {
    font-size: 15px;
  }

  .greeting-subtitle {
    font-size: 11px;
  }

  .cards-grid {
    grid-template-columns: 1fr;
    grid-auto-rows: 180px;
    gap: 10px;
  }

  .chat-input-wrap {
    padding: 6px 12px 12px;
  }
}

@media (max-width: 768px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }

  .welcome-body-inner {
    padding: 12px 12px 10px;
  }

  .chat-input-wrap {
    padding: 6px 12px 12px;
  }
}
</style>

<style lang="scss">
@media (min-width: 1260px) {
  .chat-bot-popover {
    &.fullscreen {
      .datetime-pill {
        font-size: 18px;
      }
      .new-chat-button {
        font-size: 18px;
      }
      .oliver-label {
        font-size: 20px;
      }

      .system-message {
        font-size: 19px;
      }

      .card-header {
        .card-title {
          font-size: 23px;
        }
        .view-all-link {
          font-size: 21px;
        }
        i {
          font-size: 20px;
        }
        .tab-pill {
          font-size: 18px;
        }
      }
      .card-empty,
      .todo-empty {
        font-size: 21px;
      }
      .message-row {
        .message-avatar {
          width: 70px;
          height: 70px;
        }
        .message-sender {
          font-size: 21px;
        }
        .message-time,
        .message-preview {
          font-size: 17px;
        }
      }
      .nav-item {
        font-size: 18px;
      }
      .add-todo-button {
        width: 32px;
        height: 32px;
        font-size: 16px;
      }
      .add-todo-input,
      .clients-ask-input {
        font-size: 21px;
      }
      .todo-label {
        font-size: 21px;
      }
      .todo-delete {
        font-size: 28px;
      }
      .clients-input-wrap {
        .input-icon,
        .input-chevron {
          font-size: 17px;
        }
      }
      .patient-list {
        .patient-avatar {
          width: 52px;
          height: 52px;
        }
        .patient-name {
          font-size: 17px;
        }
        .patient-sub {
          font-size: 15px;
        }
      }
      .clients-summary {
        .clients-count-circle {
          width: 60px;
          height: 60px;
        }
        .count-number {
          font-size: 21px;
        }
        .active-label {
          font-size: 22px;
        }
        .capacity-label {
          font-size: 20px;
        }
      }
      .cards-grid {
        grid-auto-rows: auto;
      }
      .cards-grid-item {
        height: 260px;
      }
      .chat-input {
        font-size: 21px;
      }
      .input-button--icon,
      .input-button--send {
        i {
          font-size: 22px;
        }
      }
      .input-button--send {
        width: 48px;
        height: 48px;
        min-width: 48px;
      }
      .suggestions-panel {
        .panel-title {
          font-size: 20px;
        }
        .panel-hint,
        .category-label,
        .prompt-icon,
        .prompt-text {
          font-size: 16px;
        }
      }
      .icon-button,
      .theme-picker-btn,
      .group-label {
        font-size: 16px;
      }
      .sidebar-title {
        font-size: 22px;
      }
      .new-conversation-button,
      .search-input,
      .chat-title {
        font-size: 20px;
      }
      .search-conversation-section {
        i {
          font-size: 20px;
        }
      }
      .empty-state {
        i {
          font-size: 50px;
        }
        p {
          font-size: 20px;
        }
        small {
          font-size: 18px;
        }
      }
      .loading-state {
        i {
          font-size: 50px;
        }
        p {
          font-size: 20px;
        }
      }
      .chat-timestamp {
        font-size: 18px;
      }
      .close-button {
        i {
          font-size: 18px;
        }
      }
      .chat-icon {
        width: 46px;
        height: 46px;
        font-size: 20px;
      }
      .upcoming-appointments-card {
        .appointment-time {
          font-size: 21px;
          width: 150px;
        }
        .appointment-name {
          font-size: 22px;
        }
        .appointment-meta {
          font-size: 20px;
        }
      }
      .assistant-message {
        font-size: 16px;
      }
      .user-message {
        font-size: 16px;
      }
      .message-timestamp,
      .copy-btn-label,
      .copy-btn,
      .disclaimer-note {
        font-size: 13px;
      }
      .specialist-markdown {
        &,
        h1,
        h2,
        h3,
        h4,
        h5,
        h6 {
          font-size: 14px;
        }
      }
      .specialist-name {
        font-size: 15px;
      }
      .queue-specialist-title {
        font-size: 18px;
      }
      .queue-empty {
        p {
          font-size: 18px;
        }
      }

      .queue-item-text {
        font-size: 16px;
      }
      .queue-item-label {
        font-size: 14px;
      }
      .queue-item-icon {
        width: 32px;
        height: 32px;
      }
      .queue-item-check {
        font-size: 14px;
      }
      .queue-pause-btn {
        font-size: 16px;
        i {
          font-size: 14px;
        }
      }
      .queue-item-remove {
        width: 30px;
        height: 30px;
        i {
          font-size: 14px;
        }
      }
      .queue-ready-line {
        font-size: 13px;
      }
      .queue-ready-icon {
        font-size: 14px;
      }
      .icon-button,
      .theme-picker-btn {
        width: 44px;
        height: 44px;
      }
      .new-chat-button {
        height: 44px;
        i {
          font-size: 14px;
        }
      }
      .action-button {
        i {
          font-size: 16px;
        }
      }
    }
  }
}

.disclaimer-note {
  width: 100%;
  text-align: right;
  font-size: 11px;
  color: var(--oliver-text);
  opacity: 1;
  padding: 4px 0 0;
  margin: 0;
}
</style>
